import { useCallback, useEffect, useRef, useState } from "react";

export interface UseCountdownOptions {
  /** Total countdown duration in milliseconds. */
  durationMs: number;
  /** Fired exactly once when the countdown reaches zero. */
  onComplete: () => void;
  /** When true the timer stops and resets; when false it starts fresh. */
  isStopped?: boolean;
  /** Prevents starting entirely — takes precedence over isStopped. */
  isDisabled?: boolean;
}

export interface UseCountdownReturn {
  /** Remaining whole seconds, updated once per displayed-second change. */
  remainingSeconds: number;
  /** True while the countdown is actively ticking. */
  isRunning: boolean;
  /** Restart the countdown from the full duration. */
  reset: () => void;
  /** Permanently stop — ignores future isStopped changes. */
  cancel: () => void;
}

/**
 * Lightweight countdown timer for "auto-action after N seconds" UIs.
 *
 * The visual progress bar is driven by CSS `transition` in the consumer
 * (typically Button), so this hook only provides `isRunning` (for toggling
 * the CSS transition) and `remainingSeconds` (for the "8s" badge).
 *
 * The tick interval runs at 200ms for precision but only triggers a React
 * state update when the displayed second actually changes, keeping
 * re-renders to ~1 per second.
 */
export function useCountdown({
  durationMs,
  onComplete,
  isStopped = false,
  isDisabled = false,
}: UseCountdownOptions): UseCountdownReturn {
  const initialSeconds = Math.ceil(durationMs / 1000);
  const [remainingSeconds, setRemainingSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref keeps `startTimers` stable regardless of inline onComplete identity,
  // preventing the countdown from restarting on every parent render.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const lastSecondRef = useRef(initialSeconds);

  const clearTimers = useCallback(() => {
    if (completionTimerRef.current != null) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    if (tickTimerRef.current != null) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();

    const seconds = Math.ceil(durationMs / 1000);
    lastSecondRef.current = seconds;
    setRemainingSeconds(seconds);
    setIsRunning(true);

    const startedAt = Date.now();

    tickTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const sec = Math.max(0, Math.ceil((durationMs - elapsed) / 1000));
      if (sec !== lastSecondRef.current) {
        lastSecondRef.current = sec;
        setRemainingSeconds(sec);
      }
    }, 200);

    completionTimerRef.current = setTimeout(() => {
      clearTimers();
      lastSecondRef.current = 0;
      setRemainingSeconds(0);
      setIsRunning(false);
      onCompleteRef.current();
    }, durationMs);
  }, [durationMs, clearTimers]);

  const stopAndReset = useCallback(() => {
    clearTimers();
    const seconds = Math.ceil(durationMs / 1000);
    lastSecondRef.current = seconds;
    setRemainingSeconds(seconds);
    setIsRunning(false);
  }, [durationMs, clearTimers]);

  // React to isStopped / isDisabled / isCancelled changes.
  useEffect(() => {
    if (isCancelled || isDisabled) {
      stopAndReset();
      return;
    }
    if (isStopped) {
      stopAndReset();
    } else {
      startTimers();
    }
    return clearTimers;
  }, [isStopped, isDisabled, isCancelled, startTimers, stopAndReset, clearTimers]);

  const reset = useCallback(() => {
    if (isCancelled || isDisabled) return;
    startTimers();
  }, [isCancelled, isDisabled, startTimers]);

  const cancel = useCallback(() => {
    setIsCancelled(true);
  }, []);

  return { remainingSeconds, isRunning, reset, cancel };
}
